import { ScrollingService } from './../../../services/scrolling.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  input,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  output,
  signal,
  effect
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { MessageEx } from '../../../classes/messageex';
import { ChatLoggingService } from '../../../services/chat-logging.service';
import { ScriptLoaderService } from '../../../services/script-loader.service';
import type { OpenLinkEvent, SendWebhookPayloadEvent } from '../../../classes/actioncardhelpers';

declare var AdaptiveCards: any;

@Component({
  selector: 'adaptive-card-renderer',
  templateUrl: './adaptive-card-renderer.component.html',
  styleUrls: ['../../chat-item/chat-item.component.scss', './adaptive-card-renderer.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdaptiveCardRendererComponent implements OnInit {
  @ViewChild('cardContainer', { static: true }) cardContainer: ElementRef;

  // Signal-based inputs
  item = input.required<MessageEx>();
  previewMode = input<boolean>(false);

  // Signal-based outputs
  onSendWebhookPayload = output<SendWebhookPayloadEvent>();
  onOpenLink = output<OpenLinkEvent>();

  // Signal-based internal state
  useFallback = signal<boolean>(false);
  scriptLoaded = signal<boolean>(false);
  componentName = 'adaptive-card-renderer';

  constructor(
    private scriptLoader: ScriptLoaderService,
    private cdRef: ChangeDetectorRef,
    private logger: ChatLoggingService,
    private translateService: TranslateService,
    private scrollingService: ScrollingService
  ) {
    // Effect to update fallbackText when item changes
    effect(() => {
      const currentItem = this.item();
      if (currentItem && !currentItem.fallbackText) {
        currentItem.fallbackText = this.translateService.instant('cl_chat.unableToDisplayContent');
      }

      if (currentItem && currentItem.fallbackText && currentItem.fallbackText.indexOf('{{type}}') > 0) {
        const updated = currentItem.fallbackText.replace('{{type}}', currentItem.contentType);
        currentItem.fallbackText = updated;
      }
    });

    // Effect to re-render when item changes (after script is loaded)
    effect(() => {
      const currentItem = this.item();
      const loaded = this.scriptLoaded();

      if (loaded && currentItem) {
        // Clear previous render
        if (this.cardContainer?.nativeElement) {
          this.cardContainer.nativeElement.innerHTML = '';
        }
        this.render();
      }
    });
  }

  async ngOnInit() {
    try {
      // load async from CDN vs packing in .. this is a new library with some weight to it.
      // this removes the need for hosts to add dependancies. until the chat micro supports large payloads this has limited use.. lets keep it dynamic until the big payloads are supported and it takes off
      // for now its a light weight renderer that doesn't even load without this specific payload
      await this.scriptLoader.load({
        name: 'adaptivecards',
        src: 'https://unpkg.com/adaptivecards/dist/adaptivecards.js'
      });

      // Mark script as loaded - this will trigger the render effect
      this.scriptLoaded.set(true);
    } catch (error) {
      this.useFallback.set(true);
      this.logger.error({
        logMessage: `AdaptiveCardRendererComponent.ngOnInit() failure ${JSON.stringify(error)}`
      });
    } finally {
      this.cdRef.markForCheck();
    }
  }

  // ngOnChanges removed - handled by effect in constructor

  private render(): void {
    let isNearBottom = this.scrollingService.isNearBottom();

    const adaptiveCard = new AdaptiveCards.AdaptiveCard();

    try {
      const parsedBody = JSON.parse(this.item().body);
      adaptiveCard.parse(parsedBody);
      adaptiveCard.onExecuteAction = (action: any) => {
        this.executeAction(action);
      };

      const renderedCard = adaptiveCard.render();
      this.cardContainer.nativeElement.append(renderedCard);

      if (isNearBottom) {
        setTimeout(() => {
          this.scrollingService.scrollToBottom();
        }, 0);
      }
    } catch (error) {
      this.useFallback.set(true);
      this.cdRef.markForCheck(); // Trigger change detection for OnPush
      this.logger.error({
        logMessage: `AdaptiveCardRendererComponent.renderCard() failure ${JSON.stringify(error)}`
      });
    }
  }

  private executeAction(action: any) {
    // first this is a strange callback object.. we need to convert by serializing out the UI type
    // the serilization causes this to convert to the simple Action interface which contains the type object
    const customAction: Action = JSON.parse(JSON.stringify(action)); // the serialize / deserialize operation gives us what we are looking for

    // switch.. today we only support 2 main actions
    switch (customAction.type) {
      case 'Action.OpenUrl':
        let openLinkEvent: OpenLinkEvent = {
          $event: null,
          url: customAction.url,
          title: customAction.title,
          behavior: null,
          componentName: this.componentName
        };
        this.onOpenLink.emit(openLinkEvent);
        break;
      case 'Action.Submit':
        let sendWebhookPayloadEvent: SendWebhookPayloadEvent = {
          method: customAction.data.method,
          url: customAction.data.url,
          payload: customAction.data.body,
          title: customAction.title,
          behavior: null,
          retry401: true,
          componentName: this.componentName
        };
        this.onSendWebhookPayload.emit(sendWebhookPayloadEvent);
        break;
      default:
        this.logger.error({
          logMessage: `AdaptiveCardRendererComponent.executeAction() unknown action ${customAction.type}`
        });
        break;
    }

    // Trigger change detection for zoneless compatibility
    // AdaptiveCards onExecuteAction callback runs outside Angular's zone
    this.cdRef.markForCheck();
  }
}

export interface Action {
  type: 'Action.OpenUrl' | 'Action.Submit';
  title: string;
  url?: string;
  data?: SubmitActionData;
}

export interface SubmitActionData {
  url: string;
  method: 'PUT' | 'POST' | 'DELETE' | 'PATCH';
  body: any;
}
